/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {HttpErrorResponse} from "@angular/common/http";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FabMessageService} from "@fabric/components/message";
import {ManagerService} from "../manager.service";
import {WorkAbroadService} from "../../work-abroad/work-abroad.service";
import {Vacation} from "../../admin/models/vacation.model";
import {MatPaginator} from "@angular/material/paginator";

@Component({
    selector: 'app-request-cancel-decision-pop-up',
    templateUrl: './request-cancel-decision-pop-up.component.html',
    styleUrls: ['./request-cancel-decision-pop-up.component.scss']
})
export class RequestCancelDecisionPopUpComponent implements OnInit {

    public vacation = {} as Vacation;
    public isPageLoading = false;
    public typeOfRequest = '';

    @ViewChild(MatPaginator) paginator: MatPaginator | undefined;

    constructor(public dialogRef: MatDialogRef<[]>,
                private readonly fabMessageService: FabMessageService,
                private readonly managerService: ManagerService,
                private readonly workAbroadService: WorkAbroadService,
                @Inject(MAT_DIALOG_DATA) public selectedData: any) {
    }

    ngOnInit(): void {
        this.vacation = this.selectedData.value;
        this.typeOfRequest=this.selectedData.tag;
    }

    /**
     * Checks whether the approve or decline button has been pressed
     * and determines whether it was a vacation or a work abroad request
     * Then calls the relevant method
     * @param data the vacation or work abroad object
     * @param tag decline/approve flag
     * @param type request type used to determine whether vacation/work abroad
     */
    public approveOrDeclineCancelRequest(data: any, tag: string, type: string) {
        if (tag == 'APPROVE' && type == 'personal') {
            this.approveCancelRequestForVacation(data);
        } else if (tag == 'DECLINE' && type == 'personal') {
            this.declineCancelRequestForVacation(data);
        } else if (tag == 'APPROVE' && type == 'workabroad') {
            this.approveCancelRequestForWorkAbroad(data);
        } else {
            this.declineCancelRequestForWorkAbroad(data);
        }
    }

    /**
     * Declines the request to cancel the work abroad
     * Resets the work abroad status to APPROVED
     * Loops through the work abroads vacations and resets the status to APPROVED
     * @param data The work abroad object
     */
    public declineCancelRequestForWorkAbroad(data: any) {
        this.isPageLoading = true;
        if (data.vacations) {
            data.vacations.forEach((request: any) => {
                if (request.requestStatus === 'CANCEL_REQUEST') {
                    this.declineCancelRequestForVacation(data);
                }
            });
        }
        data.requestStatus = 'CANCEL_DECLINED';
        this.managerService.approveWorkRequest(data).subscribe({
            next: () => {
                this.isPageLoading = false;
                this.fabMessageService.displayMessage('Success! This request has been denied');
                this.close();
            }, error: (err: HttpErrorResponse) => {
                data.requestStatus = 'PENDING';
                this.isPageLoading = false;
                this.fabMessageService.displayErrorMessage(err.error.errorMessage);
                this.close();
            }
        });
    }

    /**
     * Approves the request to cancel the work abroad, approves the
     * cancel request which sets the work abroad status to cancelled
     * and returns the work abroad and vacation allowance to the employee
     * @param data the work abroad object
     */
    public approveCancelRequestForWorkAbroad(data: any) {
        this.isPageLoading = true;
        this.workAbroadService.cancelWorkAbroadRequest(data.id).subscribe({
            next: () => {
                this.isPageLoading = false;
                this.fabMessageService.displayMessage('Delete request submitted successfully');
                this.close();
            }, error: (err: HttpErrorResponse) => {
                this.isPageLoading = false;
                this.fabMessageService.displayErrorMessage(err.error.errorMessage);
                this.close();
            }
        });
    }

    /**
     * Declines the request to cancel vacation
     * @param data the vacation object to be reset to APPROVED
     */
    public declineCancelRequestForVacation(data: any) {
        data.requestStatus = 'APPROVED';
        data.managerComment = this.selectedData.value.managerComment;
        this.isPageLoading = true;
        this.managerService.requestDeclined(data).subscribe({
            next: () => {
                this.isPageLoading = false;
                this.fabMessageService.displayMessage('successfully declined the request');
                this.close();
            }, error: (err: HttpErrorResponse) => {
                this.isPageLoading = false;
                this.fabMessageService.displayErrorMessage(err.error.errorMessage);
                this.close();

            }
        });
    }

    /**
     * Approves the request to cancel the vacation
     * Sets the work vacation status to cancelled
     * and returns the vacation allowance to the employee
     * @param data the vacation object
     */
    public approveCancelRequestForVacation(data: any) {
        this.isPageLoading = true;
        this.managerService.deleteVacationRequest(data.id).subscribe({
            next: () => {
                this.isPageLoading = false;
                this.fabMessageService.displayMessage('successfully declined the request');
                this.close();
            }, error: (err: HttpErrorResponse) => {
                this.isPageLoading = false;
                this.fabMessageService.displayErrorMessage(err.error.errorMessage);
                this.close();
            }
        });
    }

    close(): void {
        this.dialogRef.close();
    }
}
